import { FC, useEffect, useMemo, useState } from "react"
import { useGetCouncilPage } from "@/hooks/api/use-get-council-page"
import { X } from "lucide-react"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { Checkbox } from "./ui/checkbox"
import { Input } from "./ui/input"
import { Label } from "./ui/label"

export const formatCouncilDisplayName = (council: {
  id?: string | number
  name?: string
  code?: string
}) => {
  const fallback = council.id != null ? `Council ${council.id}` : "Council"
  if (council.name && council.code) return `${council.name} (${council.code})`
  return council.name ?? council.code ?? fallback
}

interface CouncilMultiSelectProps {
  value: number[]
  onChange: (value: number[]) => void
  disabledIds?: number[]
  disabled?: boolean
}

const CouncilMultiSelect: FC<CouncilMultiSelectProps> = ({
  value,
  onChange,
  disabledIds = [],
  disabled: allDisabled = false,
}) => {
  const { data, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGetCouncilPage()
  const [search, setSearch] = useState("")
  const councils = useMemo(() => data?.pages.flatMap((page) => page.content) ?? [], [data?.pages])
  const selectedCouncils = councils.filter((council) => value.includes(Number(council?.id)))
  const normalizedSearch = search.trim().toLowerCase()
  const filteredCouncils = useMemo(() => {
    if (!normalizedSearch) return councils
    return councils.filter((council) => {
      if (!council) return false
      return formatCouncilDisplayName(council).toLowerCase().includes(normalizedSearch)
    })
  }, [councils, normalizedSearch])

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading councils...</p>
  if (error) return <p className="text-sm text-destructive">Error loading councils</p>

  return (
    <div className="space-y-3 rounded-md border p-3">
      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search councils by name or code..."
        disabled={allDisabled}
      />

      {selectedCouncils.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedCouncils.map((council) => {
            const councilId = Number(council?.id)
            const disabled = allDisabled || disabledIds.includes(councilId)
            if (!council || !councilId) return null

            return (
              <Badge key={councilId} variant="secondary" className="gap-1 pr-1">
                {formatCouncilDisplayName(council)}
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 rounded-full"
                    onClick={() => onChange(value.filter((id) => id !== councilId))}
                  >
                    <X className="h-3 w-3" />
                    <span className="sr-only">Remove {formatCouncilDisplayName(council)}</span>
                  </Button>
                )}
              </Badge>
            )
          })}
        </div>
      )}

      <div className="max-h-56 space-y-1 overflow-auto">
        {filteredCouncils.map((council) => {
          const councilId = Number(council?.id)
          if (!councilId) return null
          if (!council) return null
          const checked = value.includes(councilId)
          const disabled = allDisabled || disabledIds.includes(councilId)

          return (
            <div
              key={councilId}
              className="flex items-center gap-3 rounded-md p-2 hover:bg-muted/50"
            >
              <Checkbox
                id={`council-access-${councilId}`}
                checked={checked}
                disabled={disabled}
                onCheckedChange={(nextChecked) => {
                  if (nextChecked) {
                    onChange([...new Set([...value, councilId])])
                    return
                  }
                  onChange(value.filter((id) => id !== councilId))
                }}
              />
              <Label htmlFor={`council-access-${councilId}`} className="flex-1 cursor-pointer">
                {formatCouncilDisplayName(council)}
              </Label>
            </div>
          )
        })}
        {filteredCouncils.length === 0 && (
          <p className="px-2 text-sm text-muted-foreground">No councils found.</p>
        )}
        {isFetchingNextPage && (
          <p className="px-2 text-sm text-muted-foreground">Loading more councils...</p>
        )}
      </div>
    </div>
  )
}

export default CouncilMultiSelect
